import { useState } from "react";
import { tr } from "../i18n/tr";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Loader2, Star, Send } from "lucide-react";

/**
 * In-portal evaluation form. Submits via
 * ``POST /student/sessions/{id}/submit-evaluation/{evaluation_id}``.
 */
export default function EvaluationForm({ api, sessionId, evaluation, onDone }) {
  const [answers, setAnswers] = useState({});
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const questions = evaluation.questions || [];

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(
        `/student/sessions/${sessionId}/submit-evaluation/${evaluation.evaluation_id}`,
        { answers, comment }
      );
      toast.success(tr("lms.reponse_envoyee_merci_5acf", "Reponse envoyee. Merci !"));
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
    setSubmitting(false);
  };

  const setA = (qid, v) => setAnswers({ ...answers, [qid]: v });

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="eval-form">
      {questions.length === 0 && <div className="text-sm text-slate-500">{tr("lms.aucune_question_dans_cette_f64d", "Aucune question dans cette evaluation.")}</div>}
      {questions.map((q, i) => {
        const qid = q.question_id || q.id || `q${i}`;
        return (
          <div key={qid} className="bg-[#14101c] border border-white/10 rounded-lg p-4">
            <div className="text-sm font-semibold text-white mb-3">{i + 1}. {q.text || q.title}{q.required && <span className="text-red-400"> *</span>}</div>
            {q.type === "rating" && (
              <div className="flex gap-1" data-testid={`q-${qid}-rating`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setA(qid, n)} className={"p-1 " + ((answers[qid] || 0) >= n ? "text-[var(--brand-primary,#f7c935)]" : "text-slate-600 hover:text-slate-400")}>
                    <Star className="w-7 h-7" fill={(answers[qid] || 0) >= n ? "currentColor" : "none"} />
                  </button>
                ))}
              </div>
            )}
            {q.type === "nps" && (
              <div className="flex flex-wrap gap-1" data-testid={`q-${qid}-nps`}>
                {Array.from({ length: 11 }, (_, n) => n).map((n) => (
                  <button key={n} type="button" onClick={() => setA(qid, n)}
                    className={"w-9 h-9 rounded-lg text-sm font-bold " + (answers[qid] === n
                      ? "bg-[var(--brand-primary,#f7c935)] text-[#14101c]"
                      : "bg-white/5 text-slate-400 hover:bg-white/10")}>
                    {n}
                  </button>
                ))}
              </div>
            )}
            {q.type === "yesno" && (
              <div className="flex gap-2" data-testid={`q-${qid}-yesno`}>
                {["Oui", "Non"].map((opt) => (
                  <button key={opt} type="button" onClick={() => setA(qid, opt)}
                    className={"px-4 py-2 rounded-lg text-sm font-bold " + (answers[qid] === opt
                      ? "bg-[var(--brand-primary,#f7c935)] text-[#14101c]"
                      : "bg-white/5 text-white hover:bg-white/10")}>
                    {opt}
                  </button>
                ))}
              </div>
            )}
            {q.type === "radio" && (
              <div className="space-y-1" data-testid={`q-${qid}-radio`}>
                {(q.choices || q.options || []).map((c, ci) => {
                  const val = c.value || c.label || c.text || c;
                  return (
                    <label key={ci} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer hover:bg-white/5 rounded px-2 py-1">
                      <input type="radio" name={qid} checked={answers[qid] === val} onChange={() => setA(qid, val)} />
                      <span>{c.label || c.text || c}</span>
                    </label>
                  );
                })}
              </div>
            )}
            {q.type === "checkbox" && (
              <div className="space-y-1" data-testid={`q-${qid}-checkbox`}>
                {(q.choices || q.options || []).map((c, ci) => {
                  const val = c.value || c.label || c.text || c;
                  const arr = Array.isArray(answers[qid]) ? answers[qid] : [];
                  const checked = arr.includes(val);
                  return (
                    <label key={ci} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer hover:bg-white/5 rounded px-2 py-1">
                      <input type="checkbox" checked={checked} onChange={() => {
                        const next = checked ? arr.filter((x) => x !== val) : [...arr, val];
                        setA(qid, next);
                      }} />
                      <span>{c.label || c.text || c}</span>
                    </label>
                  );
                })}
              </div>
            )}
            {(q.type === "text" || !q.type) && (
              <textarea
                rows={3} value={answers[qid] || ""} onChange={(e) => setA(qid, e.target.value)}
                placeholder={tr("lms.votre_reponse_9b3b", "Votre reponse...")}
                className="w-full bg-[#0b0811] border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-[var(--brand-primary,#f7c935)] outline-none"
                data-testid={`q-${qid}-text`}
              />
            )}
          </div>
        );
      })}

      <div>
        <div className="text-xs text-slate-400 mb-2 font-semibold">Un commentaire libre ?</div>
        <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder={tr("lms.vos_impressions_suggestions_3e30", "Vos impressions, suggestions...")}
          className="w-full bg-[#14101c] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--brand-primary,#f7c935)] outline-none"
          data-testid="eval-comment-input" />
      </div>

      <Button type="submit" disabled={submitting} className="w-full bg-[var(--brand-primary,#f7c935)] text-[#14101c] font-bold" data-testid="eval-submit-btn">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
        {submitting ? "Envoi..." : tr("lms.envoyer_ma_reponse_65a7", "Envoyer ma reponse")}
      </Button>
    </form>
  );
}
